<template>
  <div class="min-h-screen w-full flex items-center justify-center bg-orange-600 px-6 overflow-hidden relative">

    <!-- Cercles décoratifs en arrière-plan -->
    <div class="absolute top-[-80px] right-[-80px] w-72 h-72 rounded-full bg-orange-500 opacity-50 pointer-events-none" />
    <div class="absolute bottom-[-60px] left-[-60px] w-56 h-56 rounded-full bg-orange-700 opacity-40 pointer-events-none" />

    <!-- ============================================================
         VUE PRINCIPALE : CONNEXION PAR CODE
         ============================================================ -->
    <div v-if="!showRequestForm" class="w-full max-w-sm z-10 flex flex-col items-center">

      <!-- LOGO -->
      <div class="text-center mb-10">
        <h1 class="text-5xl font-black text-white tracking-tight leading-none">Termoflow</h1>
        <p class="text-orange-200 text-sm font-medium mt-1 tracking-widest uppercase">Application Mobile</p>
      </div>

      <!-- ALERTE ERREUR -->
      <div
        v-if="errorMessage"
        class="w-full mb-4 px-4 py-3 bg-red-100 border border-red-400 text-red-700 rounded-xl text-sm font-medium"
      >
        {{ errorMessage }}
      </div>

      <!-- CARD -->
      <div class="w-full bg-white rounded-2xl shadow-2xl shadow-orange-900/30 p-6">
        <p class="text-orange-600 font-bold text-lg mb-5">Entrez votre code de connexion</p>

        <!-- Input code -->
        <input
          v-model="connectionCode"
          type="text"
          placeholder="XXXX-XXXX"
          maxlength="9"
          :disabled="isLoading"
          @input="connectionCode = connectionCode.toUpperCase()"
          @keyup.enter="handleCodeSubmit"
          class="w-full border border-gray-200 rounded-xl px-4 py-4 text-xl font-bold tracking-[6px] text-center text-gray-800 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition disabled:opacity-50 disabled:cursor-not-allowed mb-4 placeholder:tracking-normal placeholder:text-gray-300 placeholder:font-normal placeholder:text-base"
        />

        <!-- Bouton connexion -->
        <button
          :disabled="!connectionCode || isLoading"
          @click="handleCodeSubmit"
          class="w-full bg-orange-600 hover:bg-orange-700 active:scale-95 text-white font-bold text-base rounded-xl py-4 flex items-center justify-center gap-2 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="isLoading" class="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          <span v-else>Se connecter</span>
        </button>

        <!-- Lien demande d'accès -->
        <button
          :disabled="isLoading"
          @click="showRequestForm = true"
          class="w-full mt-3 text-orange-600 hover:text-orange-700 font-semibold text-sm py-3 bg-transparent transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Recevoir un code de connexion
        </button>
      </div>

      <p class="mt-8 text-orange-200/80 text-xs text-center">Connexion strictement professionnelle.</p>
    </div>

    <!-- ============================================================
         VUE SECONDAIRE : DEMANDER UN CODE PAR EMAIL
         ============================================================ -->
    <div v-else class="w-full max-w-sm z-10 flex flex-col min-h-screen bg-gray-50 px-6 py-10 rounded-none sm:rounded-2xl sm:min-h-0 sm:shadow-2xl">

      <!-- RETOUR -->
      <button
        @click="closeRequestForm"
        class="flex items-center gap-1 text-orange-600 font-semibold text-sm mb-8 self-start"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-5 h-5">
          <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Retour
      </button>

      <!-- TITRE -->
      <div class="flex flex-col items-center text-center mb-8">
        <div class="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-4">
          <svg class="w-7 h-7 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
        </div>
        <h2 class="text-2xl font-black text-gray-800 mb-1">Recevoir un accès</h2>
        <p class="text-gray-500 text-sm leading-relaxed px-4">
          Entrez votre email pour recevoir votre code de connexion
        </p>
      </div>

      <!-- SUCCÈS -->
      <div
        v-if="successMessage"
        class="flex items-center gap-2 w-full mb-4 px-4 py-3 bg-green-50 border border-green-400 text-green-700 rounded-xl text-sm font-medium"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 flex-shrink-0">
          <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="12" cy="12" r="9"/>
        </svg>
        {{ successMessage }}
      </div>

      <!-- ERREUR -->
      <div
        v-if="errorMessage"
        class="w-full mb-4 px-4 py-3 bg-red-50 border border-red-400 text-red-700 rounded-xl text-sm font-medium"
      >
        {{ errorMessage }}
      </div>

      <!-- CHAMP EMAIL -->
      <div class="mb-6">
        <label class="block text-sm font-semibold text-gray-700 mb-2">Adresse email</label>
        <div class="flex items-center border border-gray-200 rounded-xl bg-white px-4 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-100 transition">
          <svg class="w-5 h-5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
          <input
            v-model="email"
            type="email"
            placeholder="votre.email@example.fr"
            autocomplete="email"
            :disabled="isLoading"
            @keyup.enter="handleRequestCode"
            class="flex-1 py-4 pl-3 bg-transparent text-gray-800 text-sm outline-none disabled:opacity-50 placeholder:text-gray-300"
          />
        </div>
      </div>

      <!-- BOUTON ENVOYER -->
      <button
        :disabled="isLoading"
        @click="handleRequestCode"
        class="w-full bg-orange-600 hover:bg-orange-700 active:scale-95 text-white font-bold text-base rounded-xl py-4 flex items-center justify-center gap-2 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg v-if="isLoading" class="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        <template v-else>
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
          Envoyer un code de connexion
        </template>
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: 'LoginView',

  data() {
    return {
      connectionCode: '',
      email: '',
      isLoading: false,
      showRequestForm: false,
      successMessage: '',
      errorMessage: '',
    };
  },

  methods: {

    /* ================================================================
     * CONNEXION PAR CODE
     * ================================================================ */

    async handleCodeSubmit() {
      if (!this.connectionCode || this.isLoading) return;

      try {
        this.isLoading = true;
        this.errorMessage = '';

        const data = await this.$request.post('/mobile/login', {
          code: this.connectionCode.toUpperCase(),
        });

        if (!data?.token) throw new Error('Token manquant dans la réponse');

        localStorage.setItem('auth-token', data.token);
        window.location.href = '/';
      } catch (error) {
        this.errorMessage = error?.message || 'Connexion impossible';
      } finally {
        this.isLoading = false;
      }
    },

    /* ================================================================
     * DEMANDE DE CODE PAR EMAIL
     * ================================================================ */

    async handleRequestCode() {
      this.errorMessage = '';

      if (!this.email.trim()) {
        this.errorMessage = 'Veuillez entrer votre adresse email';
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.errorMessage = 'Veuillez entrer une adresse email valide';
        return;
      }

      try {
        this.isLoading = true;

        const response = await this.$request.post('/mobile/send-code', {
          email: this.email,
        });

        if (response === 1) {
          this.successMessage = 'Code de connexion envoyé avec succès';
          setTimeout(() => this.closeRequestForm(), 2000);
        } else {
          this.errorMessage = response?.error || 'Impossible d\'envoyer un code de connexion';
        }
      } catch (error) {
        this.errorMessage = error?.message || 'Impossible d\'envoyer un code de connexion';
      } finally {
        this.isLoading = false;
      }
    },

    closeRequestForm() {
      this.showRequestForm = false;
      this.email = '';
      this.successMessage = '';
      this.errorMessage = '';
    },
  },
};
</script>